import React, { useEffect } from "react";
import { Button } from "./Button";
import Pricing from "./Pricing";
import Divider from "./Divider";
import { FAQ } from "./FAQ";
import { Feature } from "./Feature";
import './styles/LandingPage.css'
import { NavSections } from "../common/nav-sections";
import { APP_NAME, LANDING_PAGE_CONTAINER_ID } from "../common/constants";
import { useLocation, useNavigate } from "react-router-dom";
import SEO from "./SEO";
import { waitForImagesToLoadThenScroll } from "../common/scrollUtils";
import VideoClipper from "./VideoClipper";
import { Plan } from "../services/UserService";
import { useUsage } from "../providers/UsageProvider";
import { AppRoutes } from "../common/app-routes";

interface LandingPageProps {
    changeActiveSection: (id: string) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ changeActiveSection }) => {
    const location = useLocation();
    const usage = useUsage();
    const navigate = useNavigate();

    useEffect(() => {
        const hash = window.location.hash;
        const skipScroll = location?.state?.skipHashScroll;

        if (!hash || skipScroll) return;

        const id = hash.substring(1);

        setTimeout(() => {
            waitForImagesToLoadThenScroll(id, LANDING_PAGE_CONTAINER_ID, 1000, 3);
        }, 150);
    }, [location]);

    return (
        <>
            <SEO
                key={location.pathname}
                title={`${APP_NAME} – The all-in-one media processing app`}
                description="Cut, trim, and convert your video and audio files directly in your browser. Fast, secure, and easy to use with support for popular formats."
            />

            <div className="landing-container" id={LANDING_PAGE_CONTAINER_ID}>
                <section className="hero-section" id={NavSections.WELCOME}>
                    <h1 className="hero-title">
                        Cut, Convert & Create <span>Seamlessly</span> with {APP_NAME} 🎬
                    </h1>
                    <p className="hero-subtitle">
                        The all-in-one web app to trim, and convert your video & audio files into any format — fast, secure, and right in your browser.
                    </p>
                    <div className="hero-buttons">
                        <Button variant="default" onClick={() => {
                            navigate(AppRoutes.LOGIN);
                            changeActiveSection('login');
                        }}>
                            Get Started
                        </Button>
                    </div>
                </section>

                <Divider />

                <section className="hero-section" id={NavSections.CLIP}>
                    <VideoClipper maxProcessingMinutes={usage?.limits?.maxUsage} usedProcessingMinutes={usage?.limits?.used} changeActiveSection={changeActiveSection} />
                </section>

                <Divider />

                <section className="features-section" id={NavSections.FEATURES}>
                    <h2>✨ What you can do with {APP_NAME}</h2>
                    <div className="features-grid">
                        <Feature
                            icon="✂️"
                            title="Clip & cut precisely"
                            desc="Set exact start and end points to extract the part of your video or audio you actually need."
                        />
                        <Feature
                            icon="🔄"
                            title="Convert between formats"
                            desc="Export to MP4, MP3, WAV, AAC and more without installing heavy desktop software."
                        />
                        <Feature
                            icon="⚡"
                            title="Fast, in-browser processing"
                            desc="Handle clips quickly from any modern browser with a simple, focused interface."
                        />
                        <Feature
                            icon="🛡️"
                            title="You stay in control"
                            desc="Only process files you own or are licensed to use. You're always in charge of your content."
                        />
                        <Feature
                            icon="📂"
                            title="Great for creators & clients"
                            desc="Prepare social clips, intros, podcast snippets, and client deliverables in a few clicks."
                        />
                        <Feature
                            icon="👨‍💻"
                            title="Ready for power users"
                            desc="Pro and Developer plans unlock higher limits and advanced workflows for heavy users."
                        />
                    </div>
                </section>

                <Pricing plan={usage?.limits?.plan ?? Plan.Free} />
                <Divider />

                <section className="faq-section" id={NavSections.FAQ}>
                    <h2>❓ Frequently Asked Questions</h2>

                    <div className="faq-list">
                        <FAQ
                            q="What is Cutora?"
                            a={`${APP_NAME} is an easy-to-use online tool that lets you trim, cut, and convert video & audio files directly in your browser. No downloads, no complicated editing software — just upload, set your range, choose a format, and export.`}
                        />

                        <FAQ
                            q="Do I need to install anything?"
                            a="No. Cutora runs fully in your browser. Just upload or paste a source link, clip, convert, and download."
                        />

                        <FAQ
                            q="What formats do you support?"
                            a="We support popular formats like MP4, MP3, WAV, AAC, M4A, WEBM, and more. We are continuously adding new formats."
                        />

                        <FAQ
                            q="Can I use Cutora commercially?"
                            a="Yes — you can use the generated clips for client work, social content, personal projects, and professional use, as long as you have the legal right to use the original content."
                        />

                        <FAQ
                            q="Do I need to own the video to process it?"
                            a="You must own the content or have permission to use it. Users are fully responsible for ensuring they respect copyright laws and platform terms."
                        />

                        <FAQ
                            q="Is there a free plan?"
                            a="Yes! We offer a free tier with limited monthly processing time. Paid plans unlock higher limits and extra features."
                        />

                        <FAQ
                            q="Is my data safe?"
                            a="We only process the files needed to generate your clip. We do not sell your data, and we do not store your processed files longer than necessary."
                        />

                        <FAQ
                            q="What happens when I reach my monthly limit?"
                            a="You can wait until your quota resets or upgrade to a higher plan if you need more processing time."
                        />

                        <FAQ
                            q="Will more features be added?"
                            a="Yes. Our roadmap includes merging clips, subtitles, AI trimming suggestions"
                        />
                    </div>
                </section>

            </div>
        </>
    );
};

export default LandingPage;
